/* eslint-disable react/no-danger */
import React, { useState } from 'react';
import styled from 'styled-components';
import { SEO, Testimonials, Cards } from 'components';
import { graphql } from 'gatsby';
import { useWindowSize } from 'hooks';
import DOMPurify from 'isomorphic-dompurify';
import { getAltTagFromFileName } from 'utils';
import { Hero, Grid } from 'components/ProductPage/Shared';

function Showcases({ data }) {
  const { heroData, showcases } = data;
  const { heroImage, heroTitle, heroDescription, gradientPercentage, filter } =
    heroData.frontmatter;
  const [currentPage, setCurrentPage] = useState(1);
  const { windowWidth } = useWindowSize();

  return (
    <>
      <Hero
        height={windowWidth < 650 ? '286px' : '14.75rem'}
        heroImage={heroImage}
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Showcases"
        gradientPercentage={gradientPercentage}
        heroImageOffset={-115}
        filter={filter}
        breadcrumb="Showcases"
        removeProductNameFromPath>
        <h1 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroTitle) }} />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <Grid>
        <InnerGrid className="fade-in">
          <h2
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                windowWidth < 650 ? 'Some Of<br />Our Finest Work ' : 'Some Of Our Finest Work'
              )
            }}
          />
          <Cards
            products={showcases.nodes}
            imageWidth={360}
            imageHeight={486}
            productsPerPage={windowWidth < 823 ? 3 : 9}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </InnerGrid>
      </Grid>
      <StyledTestimonials backgroundColor={windowWidth < 590 ? '#fff' : 'var(--connolly-grey)'} />
    </>
  );
}

const InnerGrid = styled.div`
  background-color: #fff;
  border-radius: 0.375rem;
  display: grid;
  gap: 1.5rem 20px;
  grid-column: 1/-1;
  grid-row: auto auto;
  grid-template-columns: repeat(auto-fit, minmax(354px, 1fr));
  justify-content: center;
  margin-top: 0;
  padding: 1.25rem 1.25rem 4.25rem;
  z-index: 1;

  @media (width >= 590px) {
    padding: 1.25rem 1.25rem 5.438rem;
  }

  @media (width >= 650px) {
    margin-top: -35px;
  }

  @media (width >= 900px) {
    padding: 1.875rem 1.25rem 5.438rem;
  }

  @media (width >= 1000px) {
    padding: 1.875rem 1.875rem 5.438rem;
  }

  @media (width >= 1100px) {
    grid-column: 2/12;
  }

  h2 {
    font-size: 2rem;
    grid-column: 1/-1;
    grid-row: 1/2;
    margin: 1.094em auto 0.625em;
    text-align: center;
  }

  /* Cards */
  > div {
    grid-column: 1/-1;
    grid-row: 2;
    margin-bottom: unset;
    row-gap: 20px;

    a {
      grid-column: unset;
    }
  }
`;

const StyledTestimonials = styled(Testimonials)`
  > h3 {
    padding: 3.125em 0 1.25em;
  }
`;

export const query = graphql`
  query {
    heroData: markdownRemark(frontmatter: { title: { eq: "Showcases" } }) {
      id
      frontmatter {
        heroTitle
        heroDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
        gradientPercentage
        heroImageOffset
        filter
      }
    }
    showcases: allSanityShowcase(sort: { _createdAt: DESC }) {
      nodes {
        id
        title
        images {
          _key
          hotspot {
            height
            width
            x
            y
          }
          crop {
            top
            bottom
            left
            right
          }
          asset {
            _id
            originalFilename
            url
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="Project Showcases | Connolly Stairs & Doors Ireland"
      description="View our stunning showcases to see Connolly Stairs & Doors' craftsmanship in real homes. Find inspiration for your stair and door projects.">
      <script type="application/ld+json">
        {`{
          "@context": "http://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Stairs Ireland", "item": "https://www.connollystairs.ie/" },
            { "@type": "ListItem", "position": 2, "name": "Showcases", "item": "https://www.connollystairs.ie/showcases/" }
          ]
        }`}
      </script>
    </SEO>
  );
}

export default Showcases;
